import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CreditCard, Smartphone, Building, Copy, Check, AlertTriangle } from 'lucide-react';
import { PublicLayout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import { getRSVP, getAccommodation, getConfig, getPaymentInfo, createPayment } from '@/lib/api';

const PaymentPage = () => {
  const { rsvpId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState('');
  const [rsvp, setRsvp] = useState(null);
  const [accommodation, setAccommodation] = useState(null);
  const [config, setConfig] = useState(null);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [formData, setFormData] = useState({
    payment_method: '',
    amount: 0,
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        const [rsvpRes, accRes, configRes, paymentInfoRes] = await Promise.all([
          getRSVP(rsvpId),
          getAccommodation(rsvpId),
          getConfig(),
          getPaymentInfo(),
        ]);
        
        setRsvp(rsvpRes.data);
        setAccommodation(accRes.data);
        setConfig(configRes.data);
        setPaymentInfo(paymentInfoRes.data);
        
        // Calculate amount
        if (accRes.data && configRes.data) {
          let amount = 0;
          const priceMode = configRes.data.price_mode || 'per_room';
          
          if (priceMode === 'per_room') {
            amount = (accRes.data.num_rooms || 1) * (configRes.data.price_per_room || 100);
          } else if (priceMode === 'per_person') {
            amount = (accRes.data.num_people || 1) * (configRes.data.price_per_person || 50);
          } else if (priceMode === 'fixed') {
            amount = configRes.data.price_per_room || 100;
          } else if (priceMode === 'libre') {
            amount = configRes.data.minimum_amount || 30;
          }
          
          setFormData((prev) => ({ ...prev, amount }));
        }
      } catch (error) {
        console.error('Error loading data:', error);
        // If no accommodation found, redirect to confirmation
        navigate(`/confirmation/${rsvpId}`);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [rsvpId, navigate]);

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopied(field);
    toast.success('Copié !');
    setTimeout(() => setCopied(''), 2000);
  };

  const handleSubmit = async () => {
    if (!formData.payment_method) {
      toast.error('Veuillez sélectionner un mode de paiement');
      return;
    }

    setSubmitting(true);
    try {
      await createPayment({
        accommodation_id: accommodation.id,
        rsvp_id: rsvpId,
        amount: formData.amount,
        payment_method: formData.payment_method,
      });
      toast.success('Paiement enregistré !');
      navigate(`/confirmation/${rsvpId}`);
    } catch (error) {
      const message = error.response?.data?.detail || 'Une erreur est survenue';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const skipPayment = () => {
    navigate(`/confirmation/${rsvpId}`);
  };

  if (loading) {
    return (
      <PublicLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-pulse text-stone">Chargement...</div>
        </div>
      </PublicLayout>
    );
  }

  if (!accommodation || accommodation.accommodation_type === 'none') {
    navigate(`/confirmation/${rsvpId}`);
    return null;
  }

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="py-16 md:py-24 bg-terracotta-light/20">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="font-display text-4xl md:text-5xl text-charcoal mb-4" data-testid="payment-title">
              Participation Hébergement
            </h1>
            <p className="text-stone">
              Participez aux frais d'hébergement pour la nuit du mariage
            </p>
          </motion.div>
        </div>
      </section>

      {/* Payment Form */}
      <section className="section-padding">
        <div className="max-w-xl mx-auto">
          {/* Warning */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3"
          >
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800">
              En cas d'indisponibilité éventuelle de l'hébergement, un remboursement sera effectué très rapidement.
            </p>
          </motion.div>

          {/* Summary */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="card-elegant mb-6"
          >
            <h3 className="font-display text-xl text-charcoal mb-4">Récapitulatif</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-stone">Hébergement</span>
                <span className="font-medium text-charcoal">
                  {accommodation.accommodation_type === 'domaine' ? 'Domaine' : 'Hôtel'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-stone">Chambres</span>
                <span className="font-medium text-charcoal">{accommodation.num_rooms}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-stone">Personnes</span>
                <span className="font-medium text-charcoal">{accommodation.num_people}</span>
              </div>
              <div className="flex justify-between pt-2 border-t border-stone-light">
                <span className="text-charcoal font-medium">Montant à régler</span>
                <span className="text-terracotta font-bold text-lg">{formData.amount}€</span>
              </div>
            </div>
          </motion.div>

          {/* Amount (if libre) */}
          {config?.price_mode === 'libre' && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="card-elegant mb-6"
            >
              <Label className="text-charcoal font-medium">
                Montant de votre participation (minimum {config.minimum_amount}€)
              </Label>
              <Input
                data-testid="input-amount"
                type="number"
                min={config.minimum_amount}
                value={formData.amount}
                onChange={(e) => setFormData((prev) => ({ ...prev, amount: parseFloat(e.target.value) || config.minimum_amount }))}
                className="input-elegant mt-2"
              />
            </motion.div>
          )}

          {/* Payment Methods */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="card-elegant"
          >
            <h3 className="font-display text-xl text-charcoal mb-6">Mode de paiement</h3>
            
            <RadioGroup
              value={formData.payment_method}
              onValueChange={(value) => setFormData((prev) => ({ ...prev, payment_method: value }))}
              className="space-y-4"
            >
              {/* PayPal */}
              <div
                data-testid="radio-paypal"
                className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  formData.payment_method === 'paypal' ? 'border-terracotta bg-terracotta/5' : 'border-stone-light hover:border-terracotta/50'
                }`}
                onClick={() => setFormData((prev) => ({ ...prev, payment_method: 'paypal' }))}
              >
                <div className="flex items-center space-x-4">
                  <RadioGroupItem value="paypal" id="paypal" className="text-terracotta" />
                  <Label htmlFor="paypal" className="cursor-pointer flex-1">
                    <div className="flex items-center gap-2">
                      <CreditCard className="w-5 h-5 text-blue-600" />
                      <span className="font-medium text-charcoal">PayPal</span>
                    </div>
                  </Label>
                </div>
                {formData.payment_method === 'paypal' && paymentInfo?.paypal_email && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="mt-4 p-3 bg-blue-50 rounded-lg"
                  >
                    <p className="text-sm text-blue-800 mb-2">Envoyez le paiement à :</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-sm bg-white px-3 py-2 rounded">{paymentInfo.paypal_email}</code>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => copyToClipboard(paymentInfo.paypal_email, 'paypal')}
                      >
                        {copied === 'paypal' ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                    <p className="text-xs text-blue-600 mt-2">Indiquez votre nom en commentaire du paiement</p>
                  </motion.div>
                )}
              </div>

              {/* Paylib */}
              {paymentInfo?.paylib_phone && (
                <div
                  data-testid="radio-paylib"
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    formData.payment_method === 'paylib' ? 'border-terracotta bg-terracotta/5' : 'border-stone-light hover:border-terracotta/50'
                  }`}
                  onClick={() => setFormData((prev) => ({ ...prev, payment_method: 'paylib' }))}
                >
                  <div className="flex items-center space-x-4">
                    <RadioGroupItem value="paylib" id="paylib" className="text-terracotta" />
                    <Label htmlFor="paylib" className="cursor-pointer flex-1">
                      <div className="flex items-center gap-2">
                        <Smartphone className="w-5 h-5 text-green-600" />
                        <span className="font-medium text-charcoal">Paylib</span>
                      </div>
                    </Label>
                  </div>
                  {formData.payment_method === 'paylib' && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      className="mt-4 p-3 bg-green-50 rounded-lg"
                    >
                      <p className="text-sm text-green-800 mb-2">Envoyez le paiement au :</p>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 text-sm bg-white px-3 py-2 rounded">{paymentInfo.paylib_phone}</code>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => copyToClipboard(paymentInfo.paylib_phone, 'paylib')}
                        >
                          {copied === 'paylib' ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                        </Button>
                      </div>
                    </motion.div>
                  )}
                </div>
              )}

              {/* Virement */}
              <div
                data-testid="radio-virement"
                className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  formData.payment_method === 'virement' ? 'border-terracotta bg-terracotta/5' : 'border-stone-light hover:border-terracotta/50'
                }`}
                onClick={() => setFormData((prev) => ({ ...prev, payment_method: 'virement' }))}
              >
                <div className="flex items-center space-x-4">
                  <RadioGroupItem value="virement" id="virement" className="text-terracotta" />
                  <Label htmlFor="virement" className="cursor-pointer flex-1">
                    <div className="flex items-center gap-2">
                      <Building className="w-5 h-5 text-gray-600" />
                      <span className="font-medium text-charcoal">Virement bancaire</span>
                    </div>
                  </Label>
                </div>
                {formData.payment_method === 'virement' && paymentInfo?.rib && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="mt-4 p-3 bg-gray-50 rounded-lg space-y-3"
                  >
                    <p className="text-sm text-gray-800 font-medium">Coordonnées bancaires :</p>
                    {paymentInfo.rib.holder && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Titulaire</span>
                        <span className="text-sm font-medium">{paymentInfo.rib.holder}</span>
                      </div>
                    )}
                    {paymentInfo.rib.iban && (
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600">IBAN</span>
                        <code className="flex-1 text-xs bg-white px-2 py-1 rounded">{paymentInfo.rib.iban}</code>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => copyToClipboard(paymentInfo.rib.iban, 'iban')}
                        >
                          {copied === 'iban' ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                        </Button>
                      </div>
                    )}
                    {paymentInfo.rib.bic && (
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600">BIC</span>
                        <code className="flex-1 text-xs bg-white px-2 py-1 rounded">{paymentInfo.rib.bic}</code>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => copyToClipboard(paymentInfo.rib.bic, 'bic')}
                        >
                          {copied === 'bic' ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                        </Button>
                      </div>
                    )}
                    <p className="text-xs text-gray-500 mt-2">
                      Indiquez votre nom en référence du virement. Le paiement sera validé manuellement.
                    </p>
                  </motion.div>
                )}
              </div>
            </RadioGroup>
          </motion.div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row justify-between gap-4 mt-8">
            <Button
              data-testid="btn-skip-payment"
              type="button"
              variant="outline"
              onClick={skipPayment}
              className="btn-secondary"
            >
              Payer plus tard
            </Button>
            <Button
              data-testid="btn-submit-payment"
              type="button"
              onClick={handleSubmit}
              disabled={!formData.payment_method || submitting}
              className="btn-primary"
            >
              {submitting ? 'Envoi...' : 'Confirmer le paiement'}
            </Button>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default PaymentPage;
